// In-app notification inbox: new case, assignment, review/approval required, rejection, return,
// hold, due-soon/overdue, printing/packaging complete, ready-for-dispatch, dispatched.
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { WfNotification } from '@alignova/shared';
import { useLiveEvent } from '../../lib/useLive.ts';
import { PageHeader } from '../../ui/primitives.tsx';
import { wfApi } from './workflowApi.ts';
import { Btn, Empty, fmtDT } from './wfUi.tsx';

const ICON: Record<string, string> = {
  case_received: '➕', case_assigned: '👤', review_required: '🩺', approval_required: '✅', case_rejected: '⛔', case_returned: '↩️', case_on_hold: '⏸', due_soon: '⏰', overdue: '🔥', printing_complete: '🖨', packaging_complete: '📦', ready_for_dispatch: '🚚', dispatched: '✔️',
};

export function WfInboxPage() {
  const [items, setItems] = useState<WfNotification[]>([]);
  const [unread, setUnread] = useState(0);
  const [onlyUnread, setOnlyUnread] = useState(false);
  const load = useCallback(() => {
    wfApi.notifications(onlyUnread).then((r) => { setItems(r.items); setUnread(r.unread); }).catch(() => undefined);
  }, [onlyUnread]);
  useEffect(load, [load]);
  useLiveEvent('workflow.changed', load);
  const read = async (id: string) => { await wfApi.readNotification(id); load(); };
  const readAll = async () => { await wfApi.readAllNotifications(); load(); };
  return (
    <div className="space-y-3">
      <PageHeader title="Notifications" subtitle={`${unread} unread`} />
      <div className="flex items-center gap-2">
        <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={onlyUnread} onChange={(e) => setOnlyUnread(e.target.checked)} /> Unread only</label>
        <Btn tone="ghost" className="ml-auto" disabled={!unread} onClick={() => void readAll()}>Mark all read</Btn>
      </div>
      <ul className="space-y-1.5">
        {items.map((n) => (
          <li key={n.id} className={`flex items-start gap-3 rounded-lg border p-3 ${n.readAt ? 'border-line bg-surface' : 'border-accent/40 bg-accent/5'}`}>
            <span className="text-lg">{ICON[n.type] ?? '🔔'}</span>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                {n.caseId ? <Link to={`/workflow/cases/${n.caseId}`} className="font-semibold text-ink hover:underline">{n.title}</Link> : <span className="font-semibold text-ink">{n.title}</span>}
                {n.caseNumber && <span className="font-mono text-xs text-muted">{n.caseNumber}</span>}
              </div>
              {n.body && <div className="text-sm text-ink/80">{n.body}</div>}
              <div className="text-xs text-muted">{fmtDT(n.createdAt)}{n.role ? ` · to ${n.role.replace(/_/g, ' ')}` : ''}</div>
            </div>
            {!n.readAt && <button className="rounded border border-line px-2 py-0.5 text-xs hover:bg-surface-2" onClick={() => void read(n.id)}>Mark read</button>}
          </li>
        ))}
      </ul>
      {!items.length && <Empty>Nothing here.</Empty>}
    </div>
  );
}
